import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import SectionHeading from './ui/SectionHeading';
import { RevealGroup, RevealItem } from './ui/Reveal';
import project1 from '../assets/project_1.png';
import project2 from '../assets/project_2.png';
import project3 from '../assets/project_3.png';
import project4 from '../assets/project_4.png';
import project5 from '../assets/project_5.png';
import project6 from '../assets/project_6.png';
import project7 from '../assets/project_7.png';
import project8 from '../assets/project_8.png';

interface Case {
    title: string;
    description: string;
    tags: string[];
    metric: { value: string; label: string };
    image: string;
    large?: boolean;
}

const cases: Case[] = [
    {
        title: 'Automated WhatsApp Broadcast System',
        description:
            'Scrapes daily news, summarises it with GPT-4, and broadcasts tailored updates to 5,000+ subscribers via the WhatsApp API — fully hands-off.',
        tags: ['WhatsApp API', 'OpenAI', 'Python'],
        metric: { value: '5,000+', label: 'subscribers reached daily' },
        image: project1,
        large: true,
    },
    {
        title: 'Lead Capture & CRM Sync',
        description: 'Unified lead ingestion from Facebook Ads, website & LinkedIn into HubSpot with auto lead-scoring.',
        tags: ['HubSpot', 'Zapier'],
        metric: { value: '3×', label: 'faster sales response' },
        image: project2,
    },
    {
        title: 'E-commerce Inventory Sync',
        description: 'Real-time two-way sync between Shopify and warehouse stock.',
        tags: ['Shopify', 'n8n'],
        metric: { value: '-95%', label: 'stockouts' },
        image: project3,
    },
    {
        title: 'CRM ↔ MLS Integration Pipeline',
        description: 'Automated real-estate data sync between CRMs and Multiple Listing Services.',
        tags: ['n8n', 'Salesforce'],
        metric: { value: '-95%', label: 'manual data entry' },
        image: project4,
    },
    {
        title: 'AI WhatsApp Business Automation',
        description:
            '24/7 WhatsApp AI agents that handle orders, inquiries & support with smart hand-off to humans when it matters.',
        tags: ['n8n', 'OpenAI API', 'WhatsApp'],
        metric: { value: '80%', label: 'inquiries auto-resolved' },
        image: project5,
        large: true,
    },
    {
        title: 'Human-Like AI Call Agent',
        description: 'Voice agent that answers calls, takes orders & supports customers — indistinguishable from human.',
        tags: ['Voice AI', 'VoIP'],
        metric: { value: '24/7', label: 'call coverage' },
        image: project6,
    },
    {
        title: 'DMS Workflow Automation',
        description: 'Document processing that categorises & routes files by content and metadata.',
        tags: ['Make', 'OCR'],
        metric: { value: '10×', label: 'faster processing' },
        image: project7,
    },
    {
        title: 'Multi-Platform Lead Distribution',
        description: 'Intelligent routing that distributes prospects across channels by criteria & availability.',
        tags: ['Zapier', 'Pipedrive'],
        metric: { value: '100%', label: 'leads routed instantly' },
        image: project8,
    },
];

const CaseCard: React.FC<{ item: Case }> = ({ item }) => (
    <RevealItem className={item.large ? 'md:col-span-2' : ''}>
        <a
            href="#contact"
            className="group relative flex flex-col h-full overflow-hidden rounded-2xl card-surface hover:border-white/[0.14] transition-all duration-300"
        >
            <div className={`relative overflow-hidden ${item.large ? 'h-64' : 'h-48'}`}>
                <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent" />

                {/* metric badge */}
                <div className="absolute top-4 left-4 rounded-xl glass px-3 py-2">
                    <div className="font-display text-xl font-bold text-white leading-none">{item.metric.value}</div>
                    <div className="text-[11px] text-text-muted mt-1">{item.metric.label}</div>
                </div>

                {/* hover arrow */}
                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white text-navy-900 grid place-items-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                    <ArrowUpRight className="w-4 h-4" />
                </div>
            </div>

            <div className="flex flex-col flex-1 p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                    {item.tags.map((tag) => (
                        <span key={tag} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/[0.04] text-text-muted border border-white/[0.06]">
                            {tag}
                        </span>
                    ))}
                </div>
                <h3 className="font-display text-lg font-bold text-white group-hover:text-brand-cyan transition-colors">
                    {item.title}
                </h3>
                <p className="mt-2 text-sm text-text-muted leading-relaxed">{item.description}</p>
            </div>
        </a>
    </RevealItem>
);

const CaseStudies: React.FC = () => {
    return (
        <section id="work" className="relative py-24 md:py-32">
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <SectionHeading
                        align="left"
                        eyebrow="Selected work"
                        title={<>Automations that pay for <span className="text-gradient">themselves</span></>}
                        subtitle="A snapshot of systems we've shipped — each one quietly saving its owner hours, headcount, and revenue every single day."
                    />
                    <a href="#contact" className="hidden md:inline-flex items-center gap-2 text-brand-cyan font-semibold hover:gap-3 transition-all shrink-0">
                        Start your project <ArrowUpRight className="w-4 h-4" />
                    </a>
                </div>

                <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" stagger={0.06}>
                    {cases.map((item) => (
                        <CaseCard key={item.title} item={item} />
                    ))}
                </RevealGroup>
            </div>
        </section>
    );
};

export default CaseStudies;
